import React, { useEffect, useRef } from 'react';

/**
 * Grant Math Lava Lamp Background
 * Uses the Grantitude formula: R = α⋅π/(2⋅(−θ+3π))
 * Creates organic lava lamp effect with STRAIGHT LINES only
 * Fills entire background, designed to show through starfield at 15% opacity
 */

const GrantMathLavaLamp = ({ 
  params = {},
  className = '',
  style = {}
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const timeRef = useRef(0);
  const targetParamsRef = useRef({
    energy: 0.5,
    warmth: 0.6,
    complexity: 0.5,
    harmony: 0.7,
    expansion: 0.5
  });
  const currentParamsRef = useRef({ ...targetParamsRef.current });

  useEffect(() => {
    targetParamsRef.current = {
      energy: params.energy ?? 0.5,
      warmth: params.warmth ?? 0.6,
      complexity: params.complexity ?? 0.5,
      harmony: params.harmony ?? 0.7,
      expansion: params.expansion ?? 0.5
    };
  }, [params]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const ctx = canvas.getContext('2d');
    const baseAlpha = 1.2;
    const PI = Math.PI;

    const lerp = (current, target, speed = 0.008) => {
      return current + (target - current) * speed;
    };

    const drawLavaLamp = () => {
      const width = canvas.width;
      const height = canvas.height;
      const centerX = width / 2;
      const centerY = height / 2;
      const maxDimension = Math.max(width, height);
      
      const time = timeRef.current;
      
      // Smooth parameter interpolation
      const p = currentParamsRef.current;
      const t = targetParamsRef.current;
      p.energy = lerp(p.energy, t.energy);
      p.warmth = lerp(p.warmth, t.warmth);
      p.complexity = lerp(p.complexity, t.complexity);
      p.harmony = lerp(p.harmony, t.harmony);
      p.expansion = lerp(p.expansion, t.expansion);

      // Very slow fade for organic trails - 25% speed
      ctx.fillStyle = 'rgba(0, 0, 0, 0.015)';
      ctx.fillRect(0, 0, width, height);

      // SLOWED to 25% - multiply by 0.25
      const slowTime = time * 0.25;
      const baseSpeed = 0.003 + p.energy * 0.008;

      // Color palette based on warmth
      const getColor = (layerIndex, alpha) => {
        const hueShift = p.warmth * 60; // 0 = cool blues, 60 = warm oranges
        const baseHues = [
          280 - hueShift,  // Magenta -> Orange
          180 - hueShift * 0.5,  // Cyan -> Yellow-green
          60 - hueShift * 0.3,   // Yellow -> Red
          30 - hueShift * 0.2,   // Orange -> Deep red
          150 - hueShift * 0.4,  // Lime -> Gold
          270 - hueShift * 0.6,  // Purple -> Coral
        ];
        const hue = (baseHues[layerIndex % baseHues.length] + 360) % 360;
        const sat = 70 + p.energy * 30;
        const light = 50 + p.energy * 20;
        return `hsla(${hue}, ${sat}%, ${light}%, ${alpha})`;
      };

      // Number of layers based on complexity
      const numLayers = Math.floor(4 + p.complexity * 6);
      const scale = maxDimension / 300; // Scale to fill screen

      // Draw Grant Math spiral layers with STRAIGHT LINES
      for (let layerIndex = 0; layerIndex < numLayers; layerIndex++) {
        const layerPhase = (layerIndex / numLayers) * PI * 2;
        const layerSpeed = baseSpeed * (0.8 + (layerIndex % 3) * 0.2);
        const direction = layerIndex % 2 === 0 ? 1 : -1;
        
        // Primary spiral using Grant Math formula: R = α⋅π/(2⋅(−θ+3π))
        const points = [];
        const segments = Math.floor(60 + p.complexity * 80);
        
        for (let i = 0; i <= segments; i++) {
          const theta = (i / segments) * 8 * PI;
          
          // Grant Math Formula
          const denominator = 2 * (-theta + 3 * PI + layerPhase);
          let r = denominator !== 0 ? (baseAlpha * PI) / denominator : 0;
          r = Math.abs(r) * scale * (40 + p.expansion * 30);
          
          // Add organic movement
          const breathe = 1 + Math.sin(slowTime * layerSpeed * 2 + layerIndex) * 0.3 * p.expansion;
          const wobble = Math.sin(theta * 3 + slowTime * layerSpeed) * 15 * (1 - p.harmony) * scale;
          r = r * breathe + wobble;
          
          // Rotate over time
          const angle = theta * direction + slowTime * layerSpeed + layerPhase;
          
          const x = centerX + r * Math.cos(angle);
          const y = centerY + r * Math.sin(angle);
          
          points.push({ x, y });
        }

        // Draw as STRAIGHT LINE SEGMENTS (not curves)
        const alpha = (0.15 + p.energy * 0.25) * (1 - layerIndex * 0.05);
        ctx.strokeStyle = getColor(layerIndex, alpha);
        ctx.lineWidth = (3 + (numLayers - layerIndex) * 0.5) * (1 + p.energy * 0.5);
        ctx.lineCap = 'round';
        
        // Glow effect
        ctx.shadowColor = getColor(layerIndex, alpha * 0.8);
        ctx.shadowBlur = 20 + p.energy * 30;

        ctx.beginPath();
        for (let i = 0; i < points.length; i++) {
          if (i === 0) {
            ctx.moveTo(points[i].x, points[i].y);
          } else {
            // STRAIGHT LINE to next point
            ctx.lineTo(points[i].x, points[i].y);
          }
        }
        ctx.stroke();

        // Reciprocal counter-spiral (also straight lines)
        const reciprocalPoints = [];
        for (let i = 0; i <= segments; i++) {
          const theta = (i / segments) * 6 * PI;
          
          // Reciprocal Grant Math
          const denominator = 2 * (theta - 2 * PI - layerPhase);
          let r = denominator !== 0 ? (baseAlpha * 1.5 * PI) / denominator : 0;
          r = Math.abs(r) * scale * (30 + p.expansion * 25);
          
          const breathe = 1 + Math.cos(slowTime * layerSpeed * 1.5 + layerIndex * 0.5) * 0.25 * p.expansion;
          r = r * breathe;
          
          const angle = -theta * direction - slowTime * layerSpeed * 0.7 - layerPhase;
          
          const x = centerX + r * Math.cos(angle);
          const y = centerY + r * Math.sin(angle);
          
          reciprocalPoints.push({ x, y });
        }

        ctx.strokeStyle = getColor(layerIndex, alpha * 0.6);
        ctx.lineWidth = (2 + (numLayers - layerIndex) * 0.3) * (1 + p.energy * 0.3);
        ctx.shadowBlur = 15 + p.energy * 20;

        ctx.beginPath();
        for (let i = 0; i < reciprocalPoints.length; i++) {
          if (i === 0) {
            ctx.moveTo(reciprocalPoints[i].x, reciprocalPoints[i].y);
          } else {
            ctx.lineTo(reciprocalPoints[i].x, reciprocalPoints[i].y);
          }
        }
        ctx.stroke();
      }

      // Floating energy particles (lava lamp blobs)
      const numParticles = Math.floor(15 + p.complexity * 25);
      
      for (let i = 0; i < numParticles; i++) {
        const particlePhase = (i / numParticles) * PI * 2;
        const particleTime = slowTime * baseSpeed * 0.4 + particlePhase;
        
        // Orbital path using Grant Math
        const theta = particleTime * 2 + i * 0.5;
        const denominator = 2 * (-theta + 3 * PI);
        let r = denominator !== 0 ? (baseAlpha * PI) / denominator : 50;
        r = Math.abs(r) * scale * (25 + p.expansion * 35) + 50 * scale;
        
        const x = centerX + r * Math.cos(particleTime + i);
        const y = centerY + r * Math.sin(particleTime + i) * (0.6 + p.harmony * 0.4);
        
        // Organic blob size
        const blobSize = (15 + p.energy * 25 + Math.sin(particleTime * 2) * 10) * scale * 0.3;
        
        // Soft gradient blob
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, blobSize);
        const blobAlpha = 0.2 + p.energy * 0.3;
        gradient.addColorStop(0, getColor(i % 6, blobAlpha));
        gradient.addColorStop(0.5, getColor((i + 2) % 6, blobAlpha * 0.4));
        gradient.addColorStop(1, 'transparent');
        
        ctx.fillStyle = gradient;
        ctx.shadowBlur = 30;
        ctx.shadowColor = getColor(i % 6, blobAlpha * 0.5);
        ctx.beginPath();
        ctx.arc(x, y, blobSize, 0, PI * 2);
        ctx.fill();
      }

      // Central energy core
      const coreSize = (80 + p.energy * 60 + Math.sin(slowTime * baseSpeed * 3) * 20) * scale * 0.4;
      const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, coreSize);
      const coreAlpha = 0.3 + p.energy * 0.4;
      
      coreGradient.addColorStop(0, `rgba(255, 255, 255, ${coreAlpha})`);
      coreGradient.addColorStop(0.3, getColor(0, coreAlpha * 0.7));
      coreGradient.addColorStop(0.6, getColor(1, coreAlpha * 0.4));
      coreGradient.addColorStop(1, 'transparent');
      
      ctx.fillStyle = coreGradient;
      ctx.shadowBlur = 50;
      ctx.shadowColor = getColor(0, 0.5);
      ctx.beginPath();
      ctx.arc(centerX, centerY, coreSize, 0, PI * 2);
      ctx.fill();

      ctx.shadowBlur = 0;
      timeRef.current += 1;
    };

    const animate = () => {
      drawLavaLamp();
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 pointer-events-none ${className}`}
      style={{ 
        zIndex: 0,
        ...style
      }}
    />
  );
};

export default GrantMathLavaLamp;
